import React, { useState } from 'react';
import { MailOutlined, CloseOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { apiClient, type User } from '../../lib/api';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: User, invitationLink?: string) => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor'>('editor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationResult, setInvitationResult] = useState<{
    tempPassword: string;
    email: string;
    userId: string;
  } | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const result = await apiClient.inviteUser(email, role);
      
      // Check if we have the new API response format with tempPassword
      if (result.user && 'tempPassword' in result.user) {
        // Store the invitation result to show the password
        setInvitationResult({
          tempPassword: result.user.tempPassword,
          email,
          userId: result.user.id
        });
      } else {
        // Fallback to old behavior
        const newUser: User = {
          id: result.userId || crypto.randomUUID(),
          email,
          role,
          isActive: false,
          status: 'pending',
          createdAt: new Date().toISOString()
        };
        
        onSuccess(newUser, result.invitationLink);
        handleClose();
      }
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      setError(err.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('editor');
    setError(null);
    setInvitationResult(null);
    setPasswordCopied(false);
    onClose();
  };

  const copyPasswordToClipboard = async () => {
    if (invitationResult?.tempPassword) {
      try {
        await navigator.clipboard.writeText(invitationResult.tempPassword);
        setPasswordCopied(true);
        setTimeout(() => setPasswordCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const completeInvitation = () => {
    if (invitationResult) {
      const newUser: User = {
        id: invitationResult.userId,
        email: invitationResult.email,
        role,
        isActive: false,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      onSuccess(newUser);
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-9 flex items-center">
            <MailOutlined className="mr-2" />
            Invite User
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Form */}
        {!invitationResult ? (
          <form onSubmit={handleSubmit} className="p-6">
            {/* Email Field */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-7 mb-2">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                placeholder="user@example.com"
                required
                disabled={loading}
              />
            </div>

            {/* Role Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-7 mb-2">
                Role *
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="editor"
                    checked={role === 'editor'}
                    onChange={(e) => setRole(e.target.value as 'editor')}
                    className="mr-2"
                    disabled={loading}
                  />
                  <div>
                    <span className="font-medium text-gray-9">Editor</span>
                    <p className="text-sm text-gray-6">Can manage content but not users</p>
                  </div>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="admin"
                    checked={role === 'admin'}
                    onChange={(e) => setRole(e.target.value as 'admin')}
                    className="mr-2"
                    disabled={loading}
                  />
                  <div>
                    <span className="font-medium text-gray-9">Admin</span>
                    <p className="text-sm text-gray-6">Full access to content and user management</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email}
                className="flex items-center px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <MailOutlined className="mr-2" />
                    Invite
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          /* Success State - Show Generated Password */
          <div className="p-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckOutlined className="text-green-600 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-9 mb-2">Invitation Sent Successfully!</h3>
              <p className="text-gray-6">
                User has been invited with email: <strong>{invitationResult.email}</strong>
              </p>
            </div>

            {/* Generated Password Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-1 mb-4">
              <label className="block text-sm font-medium text-gray-7 mb-2">
                Generated Temporary Password
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={invitationResult.tempPassword}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  onClick={copyPasswordToClipboard}
                  className="flex items-center px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {passwordCopied ? (
                    <CheckOutlined className="text-sm" />
                  ) : (
                    <CopyOutlined className="text-sm" />
                  )}
                </button>
              </div>
              {passwordCopied && (
                <p className="text-green-600 text-xs mt-1">Password copied to clipboard!</p>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-6">
              <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
              <ol className="text-blue-800 text-sm space-y-1 list-decimal list-inside">
                <li>Copy the temporary password above</li>
                <li>Share the password with the user through a secure channel</li>
                <li>The user can log in with their email and this temporary password</li>
                <li>They will be prompted to change their password on first login</li>
              </ol>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={completeInvitation}
                className="flex items-center px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              >
                <CheckOutlined className="mr-2" />
                Complete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteUserModal;
